/* ----------------------------------------------------------------------------
 * AuraMicModeControl — the shared "How should Aura listen?" mic-mode picker
 * (owner directive 2026-07-24). Used by Settings › Aura Voice AND the signup
 * onboarding card. Reads/writes the USER-scoped account preference via the
 * self-service /v1/ai/aura/handsfree endpoint.
 *
 *   Standard   — press-to-talk only (wake never arms). The safe default.
 *   Continuous — always-listening "Hey Aura".
 *   Both       — continuous + press-to-talk.
 *
 * Enabling a wake mode shows an explicit CONSENT panel (always-on mic = terminal-
 * equipment access; consent is affirmative, informed, withdrawable). The confirm
 * tap is the user GESTURE that (a) requests the device mic permission and (b)
 * saves the preference with consent_ack — the server stamps a versioned, durable
 * consent record. If the device DENIES the mic the preference still PERSISTS
 * server-side and the device simply falls back to press-to-talk (honest note).
 * Selecting the mode invalidates ['aura-handsfree'] so the app-wide
 * HandsFreeProvider re-resolves and arms this device immediately (perm granted).
 * -------------------------------------------------------------------------- */

import { useEffect, useState } from 'react';
import { useOptionalHandsFreeContext } from './HandsFreeProvider.tsx';
import { useQueryClient } from '@tanstack/react-query';
import {
  getHandsFreePrefs,
  saveMicMode,
  HandsFreeConsentRequiredError,
} from '@/data/handsfree';
import { AURA_MIC_MODES, type AuraMicMode, deviceMicOff, setDeviceMicOff, clearRuntimeOverride } from './handsFreeConfig.ts';

const MODE_COPY: Record<AuraMicMode, { label: string; hint: string }> = {
  standard: { label: 'Standard mic', hint: 'Press-to-talk only. Aura never listens until you hold the button.' },
  continuous: { label: 'Continuous — “Hey Aura”', hint: 'Always listening for “Hey Aura”, hands-free, on any screen.' },
  both: { label: 'Both', hint: 'Say “Hey Aura” hands-free, and still press-to-talk whenever you like.' },
};

export interface AuraMicModeControlProps {
  /** compact framing for the onboarding card vs. the settings panel. */
  variant?: 'settings' | 'onboarding';
  /** notified after a successful save (e.g. to dismiss an onboarding card). */
  onChanged?: (mode: AuraMicMode) => void;
}

export function AuraMicModeControl({ variant = 'settings', onChanged }: AuraMicModeControlProps) {
  const queryClient = useQueryClient();
  const [micMode, setMicMode] = useState<AuraMicMode>('standard');
  const [loading, setLoading] = useState(true);
  // #8 device mic kill: per-device off state (popup "Turn off mic"). Revive here.
  const hfCtx = useOptionalHandsFreeContext();
  const [micKilled, setMicKilled] = useState(() => deviceMicOff());
  const [saving, setSaving] = useState(false);
  const [consentFor, setConsentFor] = useState<AuraMicMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deviceDenied, setDeviceDenied] = useState(false);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    getHandsFreePrefs(ac.signal)
      .then((p) => { if (alive) { setMicMode(p.micMode); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; ac.abort(); };
  }, []);

  async function persist(mode: AuraMicMode, consentAck: boolean) {
    // CONSENT (owner 2026-07-26): explicitly choosing Standard retires any
    // ?handsfree QA override on this device — "never listen" means never.
    if (mode === 'standard') clearRuntimeOverride();
    setSaving(true);
    setError(null);
    try {
      const saved = await saveMicMode(mode, consentAck);
      setMicMode(saved.micMode);
      setConsentFor(null);
      // App-wide provider re-resolves the mode (arms this device if perm granted).
      // IVG #wave2.4-F2: seed the cache with the SAVED value FIRST so a failed
      // refetch (retry:false) can never leave the provider armed on a stale
      // 'continuous' after the user chose 'standard'. Invalidate still refetches
      // the authoritative record, but the gate flips immediately + correctly.
      queryClient.setQueriesData({ queryKey: ['aura-handsfree'] }, saved);
      void queryClient.invalidateQueries({ queryKey: ['aura-handsfree'] });
      onChanged?.(saved.micMode);
    } catch (err) {
      if (err instanceof HandsFreeConsentRequiredError) {
        setConsentFor(mode); // server still wants consent — re-show the panel
      } else {
        setError('Could not save your microphone setting. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  function onSelect(mode: AuraMicMode) {
    if (mode === micMode || saving) return;
    setError(null);
    setDeviceDenied(false);
    if (mode === 'standard') {
      void persist('standard', false); // withdraw — no consent needed
    } else {
      setConsentFor(mode); // wake mode — require explicit consent first
    }
  }

  // The consent CONFIRM tap: a real user gesture that requests the device mic
  // permission, then saves with consent_ack. Denial persists the preference
  // anyway (device falls back to PTT) — never blocks the account setting.
  async function confirmConsent(mode: AuraMicMode) {
    setDeviceDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop()); // only needed the grant
    } catch (err) {
      if ((err as { name?: string })?.name === 'NotAllowedError') setDeviceDenied(true);
      // fall through — save the preference regardless (device can't comply now).
    }
    await persist(mode, true);
  }

  if (loading) {
    return <p className="text-sm text-text-tertiary">Loading…</p>;
  }

  return (
    <div>
      {variant === 'settings' ? (
        <div className="mb-2">
          <span className="text-xs font-medium text-text-secondary">Microphone mode</span>
          <p className="mt-0.5 text-[11px] text-text-tertiary">
            How Aura listens. This follows your account across every workspace.
          </p>
        </div>
      ) : null}

      <div role="radiogroup" aria-label="Aura microphone mode" className="space-y-2">
        {AURA_MIC_MODES.map((mode) => {
          const selected = micMode === mode;
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={saving}
              onClick={() => onSelect(mode)}
              className={[
                'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                selected ? 'border-ai bg-ai/5' : 'border-border-subtle hover:bg-inset',
                saving ? 'cursor-not-allowed opacity-70' : '',
              ].join(' ')}
            >
              <span
                aria-hidden
                className={[
                  'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  selected ? 'border-ai' : 'border-border-default',
                ].join(' ')}
              >
                {selected ? <span className="h-2 w-2 rounded-full bg-ai" /> : null}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-text-primary">{MODE_COPY[mode].label}</span>
                <span className="block text-[11px] text-text-tertiary">{MODE_COPY[mode].hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      {consentFor ? (
        <div className="mt-3 rounded-lg border border-ai/40 bg-ai/5 p-3">
          <p className="text-sm font-medium text-text-primary">Turn on “Hey Aura”?</p>
          <p className="mt-1 text-[11px] leading-relaxed text-text-secondary">
            Your microphone will listen for the words “Hey Aura”. Audio is scored on THIS device only —
            nothing is sent anywhere until you say the wake words. You can switch back to Standard any time.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void confirmConsent(consentFor)}
              className="rounded-lg bg-ai px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? 'Turning on…' : 'Turn on Hey Aura'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setConsentFor(null)}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-inset"
            >
              Not now
            </button>
          </div>
        </div>
      ) : null}

      {deviceDenied ? (
        <p className="mt-3 text-[11px] text-warning">
          Your preference is saved, but this device blocked the microphone — Aura will use press-to-talk here.
          Allow the mic in your browser to enable “Hey Aura” on this device.
        </p>
      ) : null}

      {/* #8 SETTINGS PLACEMENT (owner 2026-07-26): one-tap device mic-kill HERE,
          under the mode options, when a wake mode is active — the placement the
          owner asked for (the popup-strip copy remains too). Device-only:
          account preference untouched. */}
      {!micKilled && micMode !== 'standard' ? (
        <button
          type="button"
          onClick={() => {
            setDeviceMicOff(true);
            setMicKilled(true);
            hfCtx?.killDeviceMic();
          }}
          className="mt-3 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm font-medium text-text-secondary hover:bg-inset"
        >
          Turn off mic on this device
        </button>
      ) : null}

      {/* #8 (owner 2026-07-25): the popup "Turn off mic" disarmed THIS device only
          (the account preference above is untouched). Offer the revive here. */}
      {micKilled && micMode !== 'standard' ? (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-inset/60 p-3">
          <p className="text-[11px] leading-relaxed text-text-secondary">
            The microphone is turned off on this device. Your account preference is unchanged.
          </p>
          <button
            type="button"
            onClick={() => {
              setDeviceMicOff(false);
              setMicKilled(false);
              hfCtx?.reviveDeviceMic();
            }}
            className="shrink-0 rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-inset"
          >
            Turn back on
          </button>
        </div>
      ) : null}
      {error ? <p className="mt-3 text-[11px] text-danger">{error}</p> : null}
    </div>
  );
}
